/**
 * QC Violations — Access Control sub-tab.
 *
 * Super Admin only. Toggles which roles can access the QC hub. Backend-
 * enforced: routes return 403 for roles toggled off, and Sidebar hides
 * the nav entry via /qc-violations/my-access.
 *
 * Super Admin is HARDCODED ON (see backend service). Attempting to toggle
 * it here is disabled to prevent confusion / accidental lockout.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Loader2, RefreshCw, Save, ShieldCheck, ShieldX } from 'lucide-react';
import { toast } from 'sonner';

import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Order + human labels for the roles we surface. Any role in role_access not
// listed here still gets persisted; it just isn't rendered.
const ROLE_ROWS = [
  { key: 'super_admin', label: 'Super Admin',                 locked: true,  hint: 'Locked ON — cannot be disabled (lockout protection).' },
  { key: 'hr_manager',  label: 'HR Manager',                  locked: false, hint: 'Default ON.' },
  { key: 'hr_admin',    label: 'HR Admin',                    locked: false, hint: 'Default ON.' },
  { key: 'hr_ops',      label: 'HR Ops',                      locked: false, hint: 'Default OFF — turn on to grant access.' },
  { key: 'hr_er',       label: 'HR Employee Relations',       locked: false, hint: 'Default OFF.' },
  { key: 'hr_aud',      label: 'HR Auditor / Compliance',     locked: false, hint: 'Default OFF.' },
  { key: 'hr_gr',       label: 'HR Government Relations',     locked: false, hint: 'Default OFF.' },
  { key: 'hr_ld',       label: 'HR Learning & Development',   locked: false, hint: 'Default OFF.' },
  { key: 'hr_pc',       label: 'HR Payroll & Compensation',   locked: false, hint: 'Default OFF.' },
  { key: 'hr_ta',       label: 'HR Talent Acquisition',       locked: false, hint: 'Default OFF.' },
  { key: 'hr_sys',      label: 'HR System Administrator',     locked: false, hint: 'Default OFF.' },
  { key: 'manager',     label: 'Line Manager',                locked: false, hint: 'Default OFF.' },
  { key: 'employee',    label: 'Employee',                    locked: false, hint: 'Default OFF — self-service users.' },
];

const QCAccessControlTab = () => {
  const { token, user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  const [settings, setSettings] = useState(null);
  const [dirty, setDirty] = useState({});     // pending changes
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/qc-violations/access-settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSettings(r.data);
      setDirty({});
    } catch (e) {
      toast.error(e.response?.data?.detail?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (isSuperAdmin) fetchSettings();
  }, [isSuperAdmin, fetchSettings]);

  const effective = useMemo(() => {
    if (!settings) return {};
    return { ...settings.role_access, ...dirty };
  }, [settings, dirty]);

  const hasChanges = useMemo(() => Object.keys(dirty).length > 0, [dirty]);
  const enabledCount = useMemo(
    () => Object.values(effective).filter(Boolean).length,
    [effective],
  );

  const toggle = (key, locked) => {
    if (locked) return;
    setDirty((d) => {
      const next = { ...d };
      // If the toggle would just restore the stored value, remove it from dirty.
      const currentStored = settings?.role_access?.[key] ?? false;
      const newVal = !effective[key];
      if (newVal === currentStored) {
        delete next[key];
      } else {
        next[key] = newVal;
      }
      return next;
    });
  };

  const save = async () => {
    if (reason.trim().length < 10) {
      toast.error('Reason must be at least 10 characters.');
      return;
    }
    setSaving(true);
    try {
      const r = await axios.put(
        `${API}/qc-violations/access-settings`,
        { role_access: dirty, reason: reason.trim() },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSettings(r.data);
      setDirty({});
      setReason('');
      toast.success('QC access settings saved.');
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'object' ? d.error || 'Save failed' : d || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!isSuperAdmin) {
    return (
      <Card data-testid="qc-access-forbidden">
        <CardContent className="p-8 text-center text-slate-600">
          <ShieldX className="h-6 w-6 mx-auto mb-3 text-slate-400" />
          Super Admin only.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="qc-access-control-tab">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <ShieldCheck className="h-5 w-5 text-emerald-600" />
            QC Hub — Role Access
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-slate-600 mb-3 max-w-3xl">
            Enable or disable access to the QC Violations hub per role. Changes
            take effect immediately: routes return HTTP 403 for disabled roles,
            and the sidebar entry is hidden. Super Admin cannot be disabled.
            Ops integration endpoints (service-token authentication) are NOT
            affected by these toggles.
          </p>

          {loading ? (
            <div className="p-6 text-center text-slate-400">
              <Loader2 className="h-5 w-5 animate-spin mx-auto" />
            </div>
          ) : !settings ? (
            <div className="text-sm text-slate-500">No settings.</div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
                {ROLE_ROWS.map((row) => {
                  const enabled = !!effective[row.key];
                  const changed = row.key in dirty;
                  return (
                    <div
                      key={row.key}
                      className={
                        'flex items-start justify-between gap-3 p-3 rounded-md border ' +
                        (changed
                          ? 'bg-amber-50 border-amber-200'
                          : enabled
                          ? 'bg-emerald-50 border-emerald-200'
                          : 'bg-slate-50 border-slate-200')
                      }
                      data-testid={`qc-access-row-${row.key}`}
                    >
                      <div>
                        <div className="text-sm font-medium text-slate-900">
                          {row.label}
                          {row.locked && (
                            <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-white">
                              LOCKED
                            </span>
                          )}
                          {changed && (
                            <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-amber-600 text-white">
                              CHANGED
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-slate-500 mt-0.5">{row.hint}</div>
                      </div>
                      <Switch
                        checked={enabled}
                        disabled={row.locked || saving}
                        onCheckedChange={() => toggle(row.key, row.locked)}
                        data-testid={`qc-access-toggle-${row.key}`}
                      />
                    </div>
                  );
                })}
              </div>

              <div className="border-t pt-3 flex flex-col gap-2">
                <div className="text-xs text-slate-500">
                  {enabledCount} role(s) currently enabled.
                  {settings.updated_at && (
                    <>
                      {' '}Last updated {settings.updated_at.slice(0, 19).replace('T', ' ')}
                      {settings.updated_by_email ? ` by ${settings.updated_by_email}` : ''}.
                    </>
                  )}
                </div>
                <Textarea
                  rows={2}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Reason for this change (≥10 chars, required for the audit trail)…"
                  data-testid="qc-access-reason-input"
                />
                <div className="flex gap-2">
                  <Button
                    onClick={save}
                    disabled={!hasChanges || saving || reason.trim().length < 10}
                    data-testid="qc-access-save-btn"
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    {saving
                      ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                      : <Save className="h-4 w-4 mr-1.5" />}
                    Save changes
                  </Button>
                  <Button
                    variant="outline"
                    onClick={fetchSettings}
                    disabled={loading || saving}
                    data-testid="qc-access-refresh-btn"
                  >
                    <RefreshCw className="h-4 w-4 mr-1.5" />
                    Discard & reload
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default QCAccessControlTab;
